import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppHeader from '../components/layout/AppHeader';
import { supabase, getUser, getJWT, apiGet } from '../auth';
import { X, Check, AlertTriangle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { getSubscriptionStatus } from '../services/subscriptionService';
import { useTheme } from '../contexts/ThemeContext';

interface ProfileProps {
    onLogout: () => void;
}

const Profile: React.FC<ProfileProps> = ({ onLogout }) => {
    const navigate = useNavigate();
    const { lang, setLang, languageManager } = useLanguage();
    const { theme: currentTheme, setTheme } = useTheme();
    const uiTheme = ['modern', 'ocean', 'sunset', 'classic'].includes(currentTheme) ? 'dark' : 'light';

    const handleThemeToggle = () => {
        setTheme(uiTheme === 'light' ? 'modern' : 'light-pro');
    };

    const getInitialLashingMode = (): 'msl' | 'cordstrap' => {
        try {
            const direct = localStorage.getItem('lashing_display_mode');
            if (direct === 'msl' || direct === 'cordstrap') return direct;
            const stored = localStorage.getItem('LASHING_CALCULATOR_STATE') || localStorage.getItem('lashing_calculator_state');
            if (stored) {
                const parsed = JSON.parse(stored);
                if (parsed.lashingDisplayMode === 'msl' || parsed.lashingDisplayMode === 'cordstrap') {
                    return parsed.lashingDisplayMode;
                }
            }
        } catch {}
        return 'msl';
    };

    const getInitialPdfTheme = (): 'teal' | 'navy' | 'standart' => {
        try {
            const direct = localStorage.getItem('pdf_theme');
            if (direct === 'teal' || direct === 'navy' || direct === 'standart') return direct;
            const stored = localStorage.getItem('LASHING_CALCULATOR_STATE') || localStorage.getItem('lashing_calculator_state');
            if (stored) {
                const parsed = JSON.parse(stored);
                if (parsed.pdfTheme === 'teal' || parsed.pdfTheme === 'navy' || parsed.pdfTheme === 'standart') {
                    return parsed.pdfTheme;
                }
            }
        } catch {}
        return 'teal';
    };

    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState<any>(null);
    const [profile, setProfile] = useState(() => ({
        company_name: '',
        address: '',
        phone: '',
        tax_office: '',
        tax_number: '',
        company_email: '',
        logo_url: '',
        terms_accepted_at: null as string | null,
        lashing_display_mode: getInitialLashingMode(),
        pdf_theme: getInitialPdfTheme()
    }));



    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
    const [showTermsModal, setShowTermsModal] = useState(false);
    const [termsAgreed, setTermsAgreed] = useState(false);
    const [isAccepting, setIsAccepting] = useState(false);
    const [modalError, setModalError] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    const handleAcceptTerms = async () => {
        setIsAccepting(true);
        setModalError(null);
        try {
            const currentUser = getUser();
            if (!currentUser) throw new Error('No user logged in');

            const timestamp = new Date().toISOString();
            const { error } = await supabase
                .from('user_profiles')
                .upsert({
                    id: currentUser.sub || currentUser.id,
                    email: currentUser.email,
                    terms_accepted_at: timestamp,
                    terms_accepted: true,
                    updated_at: new Date().toISOString()
                });

            if (error) throw error;

            try {
                localStorage.setItem('terms_accepted', 'true');
            } catch {}

            setProfile(prev => ({ ...prev, terms_accepted_at: timestamp }));
            setShowTermsModal(false);
            setMessage({ type: 'success', text: 'Terms accepted successfully!' });
        } catch (error: any) {
            console.error('Error accepting terms:', error);
            setModalError(error.message || 'Error accepting terms');
        } finally {
            setIsAccepting(false);
        }
    };



    const [status, setStatus] = useState<any>(null);

    useEffect(() => {
        loadData();
    }, []);

    const ensureSupabaseSession = async () => {
        try {
            const { data } = await supabase.auth.getSession();
            const token = getJWT();
            const refreshToken = localStorage.getItem('refresh_token');
            if (token && refreshToken && (!data?.session || data.session.access_token !== token)) {
                await supabase.auth.setSession({ access_token: token, refresh_token: refreshToken });
            }
        } catch (e) {
            console.warn('Session sync warning:', e);
        }
    };

    const loadData = async () => {
        setLoading(true);
        try {
            const currentUser = getUser();
            if (!currentUser) {
                navigate('/login');
                return;
            }
            setUser(currentUser);

            // Ensure supabase session is active before querying
            await ensureSupabaseSession();

            // Fetch both in parallel with error resilience
            const [profileRes, statusData] = await Promise.all([
                supabase
                    .from('user_profiles')
                    .select('*')
                    .eq('id', currentUser.sub || currentUser.id)
                    .maybeSingle()
                    .catch(err => {
                        console.warn('Profile fetch error:', err);
                        return { data: null, error: err };
                    }),
                getSubscriptionStatus().catch(err => {
                    console.warn('Subscription status fetch error:', err);
                    return null;
                })
            ]);

            // Handle Profile Data
            const { data, error } = profileRes || {};
            if (error && error.code !== 'PGRST116') {
                console.error('Error fetching profile:', error);
            }

            let finalLogoUrl = data?.logo_url || '';
            let finalCompanyName = data?.company_name || '';
            let finalAddress = data?.address || '';
            let finalPhone = data?.phone || '';
            let finalCompanyEmail = data?.company_email || '';
            
            // 2) Try to fetch company info via backend API (matches PDF logic and bypasses RLS)
            try {
                const companyData = await apiGet<any>('/api/profile/company');
                if (companyData) {
                    if (companyData.logo_url) finalLogoUrl = companyData.logo_url;
                    if (companyData.name) finalCompanyName = companyData.name;
                    if (companyData.address) finalAddress = companyData.address;
                    if (companyData.phone) finalPhone = companyData.phone;
                    if (companyData.email) finalCompanyEmail = companyData.email;
                }
            } catch (err) {
                console.warn('Could not fetch company data from backend API, falling back to profile data:', err);
            }

            // Local fallback for lashing mode and pdf theme if missing from remote profile
            let localLashingMode: 'msl' | 'cordstrap' | null = null;
            let localPdfTheme: 'teal' | 'navy' | 'standart' | null = null;
            try {
                const directMode = localStorage.getItem('lashing_display_mode');
                if (directMode === 'msl' || directMode === 'cordstrap') localLashingMode = directMode;
                const directTheme = localStorage.getItem('pdf_theme');
                if (directTheme === 'teal' || directTheme === 'navy' || directTheme === 'standart') localPdfTheme = directTheme;

                const stored = localStorage.getItem('LASHING_CALCULATOR_STATE') || localStorage.getItem('lashing_calculator_state');
                if (stored) {
                    const parsed = JSON.parse(stored);
                    if (!localLashingMode && (parsed.lashingDisplayMode === 'msl' || parsed.lashingDisplayMode === 'cordstrap')) {
                        localLashingMode = parsed.lashingDisplayMode;
                    }
                    if (!localPdfTheme && (parsed.pdfTheme === 'teal' || parsed.pdfTheme === 'navy' || parsed.pdfTheme === 'standart')) {
                        localPdfTheme = parsed.pdfTheme;
                    }
                }
            } catch {}

            const resolvedLashingMode = (localLashingMode === 'msl' || localLashingMode === 'cordstrap')
                ? localLashingMode
                : ((data?.lashing_display_mode as ('msl' | 'cordstrap')) || 'msl');

            const resolvedPdfTheme = (localPdfTheme === 'teal' || localPdfTheme === 'navy' || localPdfTheme === 'standart')
                ? localPdfTheme
                : ((data?.pdf_theme as ('teal' | 'navy' | 'standart')) || 'teal');

            try {
                localStorage.setItem('lashing_display_mode', resolvedLashingMode);
                localStorage.setItem('pdf_theme', resolvedPdfTheme);
            } catch {}

            // If local choice differs from DB, sync DB in background
            const userId = currentUser.sub || currentUser.id;
            if (data && localLashingMode && data.lashing_display_mode !== localLashingMode && userId) {
                supabase.from('user_profiles').update({
                    lashing_display_mode: localLashingMode,
                    updated_at: new Date().toISOString()
                }).eq('id', userId).then();
            }
            if (data && localPdfTheme && data.pdf_theme !== localPdfTheme && userId) {
                supabase.from('user_profiles').update({
                    pdf_theme: localPdfTheme,
                    updated_at: new Date().toISOString()
                }).eq('id', userId).then();
            }

            if (data?.terms_accepted || data?.terms_accepted_at) {
                try {
                    localStorage.setItem('terms_accepted', 'true');
                } catch {}
            }

            setProfile({
                company_name: finalCompanyName,
                address: finalAddress,
                phone: finalPhone,
                tax_office: data?.tax_office || '',
                tax_number: data?.tax_number || '',
                company_email: finalCompanyEmail,
                logo_url: finalLogoUrl,
                terms_accepted_at: data?.terms_accepted_at || null,
                lashing_display_mode: resolvedLashingMode,
                pdf_theme: resolvedPdfTheme
            });
            
            if (finalLogoUrl) {
                setPreviewUrl(finalLogoUrl);
            }

            // Handle Status Data
            if (statusData) {
                setStatus(statusData);
            }

        } catch (error) {
            console.error('Error loading profile data:', error);
            setProfile(prev => ({
                ...prev,
                lashing_display_mode: getInitialLashingMode(),
                pdf_theme: getInitialPdfTheme()
            }));
        } finally {
            setLoading(false);
        }
    };

    const handleLashingModeChange = async (mode: 'msl' | 'cordstrap') => {
        const currentUser = getUser();
        if (!currentUser) return;
        setIsSaving(true);
        setMessage(null);
        try {
            setProfile(prev => ({ ...prev, lashing_display_mode: mode }));

            // Sync with localStorage immediately
            try {
                const stored = localStorage.getItem('LASHING_CALCULATOR_STATE') || localStorage.getItem('lashing_calculator_state');
                const state = stored ? JSON.parse(stored) : {};
                state.lashingDisplayMode = mode;
                localStorage.setItem('LASHING_CALCULATOR_STATE', JSON.stringify(state));
                localStorage.setItem('lashing_display_mode', mode);
                localStorage.setItem('lashing_mode_timestamp', Date.now().toString());
                window.dispatchEvent(new CustomEvent('lashing_mode_changed', { detail: mode }));
            } catch (storageErr) {
                console.warn('Could not sync lashing mode to localStorage:', storageErr);
            }

            await ensureSupabaseSession();

            const userId = currentUser.sub || currentUser.id;
            // 1. Try updating existing record first
            const { error: updateError, data: updateData } = await supabase
                .from('user_profiles')
                .update({
                    lashing_display_mode: mode,
                    updated_at: new Date().toISOString()
                })
                .eq('id', userId)
                .select();

            // 2. If no row updated, fallback to upsert
            if (updateError || !updateData || updateData.length === 0) {
                const { error: upsertError } = await supabase
                    .from('user_profiles')
                    .upsert({
                        id: userId,
                        lashing_display_mode: mode,
                        updated_at: new Date().toISOString()
                    }, { onConflict: 'id' });

                if (upsertError) throw upsertError;
            }

            setMessage({ type: 'success', text: languageManager.profile.success_message });
        } catch (err: any) {
            console.error('Error updating lashing mode:', err);
            setMessage({ type: 'error', text: err.message || 'Update failed' });
        } finally {
            setIsSaving(false);
        }
    };

    const handlePdfThemeChange = async (theme: 'teal' | 'navy' | 'standart') => {
        const currentUser = getUser();
        if (!currentUser) return;
        setIsSaving(true);
        setMessage(null);
        try {
            setProfile(prev => ({ ...prev, pdf_theme: theme }));

            // Sync with localStorage immediately
            try {
                const stored = localStorage.getItem('LASHING_CALCULATOR_STATE') || localStorage.getItem('lashing_calculator_state');
                const state = stored ? JSON.parse(stored) : {};
                state.pdfTheme = theme;
                localStorage.setItem('LASHING_CALCULATOR_STATE', JSON.stringify(state));
                localStorage.setItem('pdf_theme', theme);
                localStorage.setItem('pdf_theme_timestamp', Date.now().toString());
                window.dispatchEvent(new CustomEvent('pdf_theme_changed', { detail: theme }));
            } catch (storageErr) {
                console.warn('Could not sync PDF theme to localStorage:', storageErr);
            }

            await ensureSupabaseSession();

            const userId = currentUser.sub || currentUser.id;
            const { error: updateError, data: updateData } = await supabase
                .from('user_profiles')
                .update({
                    pdf_theme: theme,
                    updated_at: new Date().toISOString()
                })
                .eq('id', userId)
                .select();

            if (updateError || !updateData || updateData.length === 0) {
                const { error: upsertError } = await supabase
                    .from('user_profiles')
                    .upsert({
                        id: userId,
                        pdf_theme: theme,
                        updated_at: new Date().toISOString()
                    }, { onConflict: 'id' });

                if (upsertError) throw upsertError;
            }

            setMessage({ type: 'success', text: languageManager.profile.success_message });
        } catch (err: any) {
            console.error('Error updating theme:', err);
            setMessage({ type: 'error', text: err.message || 'Update failed' });
        } finally {
            setIsSaving(false);
        }
    };








    return (
        <div className="min-h-screen bg-gray-50 pb-6 dark:bg-slate-900 transition-colors duration-300">
            <AppHeader
                lang={lang}
                setLang={setLang}
                onLogout={onLogout}
                theme={uiTheme}
                onToggleTheme={handleThemeToggle}
            />

            <main className="max-w-2xl mx-auto mt-4 px-4">
                <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg overflow-hidden transition-colors duration-300">
                    <div className="bg-gradient-to-r from-blue-900 to-blue-800 px-4 py-2 flex justify-between items-center">
                        <div>
                            <h1 className="text-lg font-bold text-white">{languageManager.profile.title}</h1>
                            <p className="text-blue-100 text-xs mt-0.5">{languageManager.profile.subtitle}</p>
                        </div>
                        <button
                            onClick={() => navigate('/app')}
                            className="bg-white/10 hover:bg-white/20 p-1.5 rounded-full transition-colors text-white"
                            aria-label="Close"
                        >
                            <X size={20} />
                        </button>
                    </div>

                    <div className="p-3 text-slate-700 dark:text-slate-300 space-y-4">
                        {/* Credit Usage Section */}
                        {loading ? (
                            <div className="mb-10 animate-pulse">
                                <div className="h-8 bg-slate-200 dark:bg-slate-700 w-48 rounded-md mb-6"></div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="bg-slate-50 dark:bg-slate-900/50 p-5 rounded-2xl border border-slate-100 dark:border-slate-700 h-32"></div>
                                    <div className="bg-slate-50 dark:bg-slate-900/50 p-5 rounded-2xl border border-slate-100 dark:border-slate-700 h-32"></div>
                                </div>
                            </div>
                        ) : status && status.credits && (
                            <div className="mb-2">
                                <h2 className="text-sm font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-700 to-blue-500 dark:from-blue-400 dark:to-blue-200 mb-2 flex items-center">
                                    <Check className="mr-1.5 text-blue-500" size={16} />
                                    {languageManager.profile.credits_title}
                                </h2>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                    {/* Calculation Credits */}
                                    <div className="bg-slate-50 dark:bg-slate-900/50 p-2.5 rounded-xl border border-slate-100 dark:border-slate-700 shadow-sm flex flex-col justify-between">
                                        <div className="flex justify-between items-start mb-1.5">
                                            <div className="bg-blue-100 dark:bg-blue-900/40 p-1 rounded-lg">
                                                <AlertTriangle className="text-blue-600 dark:text-blue-400" size={14} />
                                            </div>
                                            <div className="text-right">
                                                <span className="text-lg font-black text-slate-800 dark:text-white leading-none">
                                                    {status.credits.calculation_credits}
                                                </span>
                                                <p className="text-[8px] uppercase tracking-wider font-bold text-slate-400 mt-0.5">
                                                    {languageManager.profile.remaining}
                                                </p>
                                            </div>
                                        </div>
                                        <h3 className="font-bold text-xs text-slate-700 dark:text-slate-200 mb-1">
                                            {languageManager.profile.calc_credits}
                                        </h3>
                                        <div className="w-full bg-slate-200 dark:bg-slate-700 h-1 rounded-full overflow-hidden mb-1.5">
                                            <div
                                                className="bg-blue-600 h-full rounded-full transition-all duration-1000"
                                                style={{ width: `${Math.min(100, (status.credits.calculation_credits / 500) * 100)}%` }}
                                            />
                                        </div>
                                        <div className="flex justify-between text-[10px] font-medium text-slate-500">
                                            <span>{languageManager.profile.monthly_increment}: +{status.credits.monthly_calc_increment}</span>
                                            <span>{languageManager.profile.next_increment}: {new Date(status.credits.next_increment_at).toLocaleDateString()}</span>
                                        </div>
                                    </div>

                                    {/* PDF Credits */}
                                    <div className="bg-slate-50 dark:bg-slate-900/50 p-2.5 rounded-xl border border-slate-100 dark:border-slate-700 shadow-sm flex flex-col justify-between">
                                        <div className="flex justify-between items-start mb-1.5">
                                            <div className="bg-purple-100 dark:bg-purple-900/40 p-1 rounded-lg">
                                                <Check className="text-purple-600 dark:text-purple-400" size={14} />
                                            </div>
                                            <div className="text-right">
                                                <span className="text-lg font-black text-slate-800 dark:text-white leading-none">
                                                    {status.credits.pdf_credits}
                                                </span>
                                                <p className="text-[8px] uppercase tracking-wider font-bold text-slate-400 mt-0.5">
                                                    {languageManager.profile.remaining}
                                                </p>
                                            </div>
                                        </div>
                                        <h3 className="font-bold text-xs text-slate-700 dark:text-slate-200 mb-1">
                                            {languageManager.profile.pdf_credits}
                                        </h3>
                                        <div className="w-full bg-slate-200 dark:bg-slate-700 h-1 rounded-full overflow-hidden mb-1.5">
                                            <div
                                                className="bg-purple-600 h-full rounded-full transition-all duration-1000"
                                                style={{ width: `${Math.min(100, (status.credits.pdf_credits / 50) * 100)}%` }}
                                            />
                                        </div>
                                        <div className="flex justify-between text-[10px] font-medium text-slate-500">
                                            <span>{languageManager.profile.monthly_increment}: +{status.credits.monthly_pdf_increment}</span>
                                            <span>{languageManager.profile.next_increment}: {new Date(status.credits.next_increment_at).toLocaleDateString()}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Lashing Display Mode Toggle */}
                        {!loading && (
                            <div className="mb-2">
                                <h2 className="text-sm font-bold bg-clip-text text-transparent bg-gradient-to-r from-green-700 to-green-500 dark:from-green-400 dark:to-green-200 mb-2 flex items-center">
                                    <Check className="mr-1.5 text-green-500" size={16} />
                                    {languageManager.profile.lashing_display_label}
                                </h2>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    <button
                                        type="button"
                                        disabled={isSaving || loading}
                                        onClick={() => handleLashingModeChange('msl')}
                                        className={`p-4 rounded-xl border-2 text-left transition-all duration-300 ${profile.lashing_display_mode === 'msl'
                                            ? 'border-green-500 bg-green-50 dark:bg-green-500/10 ring-2 ring-green-200 dark:ring-green-500/20'
                                            : 'border-slate-200 dark:border-slate-700 hover:border-green-300 dark:hover:border-green-600'
                                            } ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    >
                                        <div className="flex items-center gap-2.5">
                                            <div className={`w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center ${profile.lashing_display_mode === 'msl'
                                                ? 'border-green-500'
                                                : 'border-slate-300 dark:border-slate-600'
                                                }`}>
                                                {profile.lashing_display_mode === 'msl' && (
                                                    <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                                                )}
                                            </div>
                                            <div>
                                                <p className="font-bold text-slate-800 dark:text-white text-xs">
                                                    {languageManager.profile.lashing_display_msl}
                                                </p>
                                                <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">
                                                    MSL 3750daN (HDB12L)
                                                </p>
                                            </div>
                                        </div>
                                    </button>
                                    <button
                                        type="button"
                                        disabled={isSaving || loading}
                                        onClick={() => handleLashingModeChange('cordstrap')}
                                        className={`p-4 rounded-xl border-2 text-left transition-all duration-300 ${profile.lashing_display_mode === 'cordstrap'
                                            ? 'border-green-500 bg-green-50 dark:bg-green-500/10 ring-2 ring-green-200 dark:ring-green-500/20'
                                            : 'border-slate-200 dark:border-slate-700 hover:border-green-300 dark:hover:border-green-600'
                                            } ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    >
                                        <div className="flex items-center gap-2.5">
                                            <div className={`w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center ${profile.lashing_display_mode === 'cordstrap'
                                                ? 'border-green-500'
                                                : 'border-slate-300 dark:border-slate-600'
                                                }`}>
                                                {profile.lashing_display_mode === 'cordstrap' && (
                                                    <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                                                )}
                                            </div>
                                            <div>
                                                <p className="font-bold text-slate-800 dark:text-white text-xs">
                                                    {languageManager.profile.lashing_display_cordstrap}
                                                </p>
                                                <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">
                                                    CL200 HDB12L
                                                </p>
                                            </div>
                                        </div>
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* PDF Theme Toggle */}
                        {!loading && (
                            <div className="mb-2">
                                <h2 className="text-sm font-bold bg-clip-text text-transparent bg-gradient-to-r from-teal-700 to-teal-500 dark:from-teal-400 dark:to-teal-200 mb-2 flex items-center">
                                    <Check className="mr-1.5 text-teal-500" size={16} />
                                    {languageManager.profile.pdf_theme_title}
                                </h2>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                                    <button
                                        type="button"
                                        disabled={isSaving || loading}
                                        onClick={() => handlePdfThemeChange('teal')}
                                        className={`p-3 rounded-xl border-2 text-left transition-all duration-300 ${profile.pdf_theme === 'teal'
                                            ? 'border-teal-500 bg-teal-50 dark:bg-teal-500/10 ring-2 ring-teal-200 dark:ring-teal-500/20'
                                            : 'border-slate-200 dark:border-slate-700 hover:border-teal-300 dark:hover:border-teal-600'
                                            } ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    >
                                        <div className="flex items-center gap-2.5">
                                            <div className={`w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center ${profile.pdf_theme === 'teal'
                                                ? 'border-teal-500'
                                                : 'border-slate-300 dark:border-slate-600'
                                                }`}>
                                                {profile.pdf_theme === 'teal' && (
                                                    <div className="w-1.5 h-1.5 rounded-full bg-teal-500" />
                                                )}
                                            </div>
                                            <div>
                                                <p className="font-bold text-slate-800 dark:text-white text-xs">
                                                    {languageManager.profile.theme_teal_title}
                                                </p>
                                                <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">
                                                    {languageManager.profile.theme_teal_desc}
                                                </p>
                                            </div>
                                        </div>
                                    </button>
                                    <button
                                        type="button"
                                        disabled={isSaving || loading}
                                        onClick={() => handlePdfThemeChange('navy')}
                                        className={`p-3 rounded-xl border-2 text-left transition-all duration-300 ${profile.pdf_theme === 'navy'
                                            ? 'border-blue-800 bg-blue-50 dark:bg-blue-900/10 ring-2 ring-blue-200 dark:ring-blue-900/20'
                                            : 'border-slate-200 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-800'
                                            } ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    >
                                        <div className="flex items-center gap-2.5">
                                            <div className={`w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center ${profile.pdf_theme === 'navy'
                                                ? 'border-blue-800'
                                                : 'border-slate-300 dark:border-slate-600'
                                                }`}>
                                                {profile.pdf_theme === 'navy' && (
                                                    <div className="w-1.5 h-1.5 rounded-full bg-blue-800" />
                                                )}
                                            </div>
                                            <div>
                                                <p className="font-bold text-slate-800 dark:text-white text-xs">
                                                    {languageManager.profile.theme_navy_title}
                                                </p>
                                                <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">
                                                    {languageManager.profile.theme_navy_desc}
                                                </p>
                                            </div>
                                        </div>
                                    </button>
                                    <button
                                        type="button"
                                        disabled={isSaving || loading}
                                        onClick={() => handlePdfThemeChange('standart')}
                                        className={`p-3 rounded-xl border-2 text-left transition-all duration-300 ${profile.pdf_theme === 'standart'
                                            ? 'border-slate-800 bg-slate-100 dark:bg-slate-700 ring-2 ring-slate-300 dark:ring-slate-600'
                                            : 'border-slate-200 dark:border-slate-700 hover:border-slate-400 dark:hover:border-slate-500'
                                            } ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    >
                                        <div className="flex items-center gap-2.5">
                                            <div className={`w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center ${profile.pdf_theme === 'standart'
                                                ? 'border-slate-800 dark:border-slate-300'
                                                : 'border-slate-300 dark:border-slate-600'
                                                }`}>
                                                {profile.pdf_theme === 'standart' && (
                                                    <div className="w-1.5 h-1.5 rounded-full bg-slate-800 dark:bg-slate-300" />
                                                )}
                                            </div>
                                            <div>
                                                <p className="font-bold text-slate-800 dark:text-white text-xs">
                                                    {languageManager.profile.theme_standart_title}
                                                </p>
                                                <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">
                                                    {languageManager.profile.theme_standart_desc}
                                                </p>
                                            </div>
                                        </div>
                                    </button>
                                </div>
                            </div>
                        )}

                        {message && (
                            <div className={`mb-6 p-4 rounded-lg flex items-center ${message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                                {message.type === 'success' ? <Check className="mr-2" /> : <AlertTriangle className="mr-2" />}
                                {message.text}
                            </div>
                        )}

                        <form className={`space-y-3 ${loading ? 'animate-pulse' : ''}`}>
                            {/* Logo Section */}
                            <div className="flex items-center space-x-4 pb-4 border-b border-gray-100">
                                <div className="flex-shrink-0">
                                    <div className="h-16 w-16 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center bg-gray-50 overflow-hidden relative">
                                        {loading ? (
                                            <div className="h-full w-full bg-slate-100 dark:bg-slate-800"></div>
                                        ) : previewUrl ? (
                                            <img src={previewUrl} alt="Logo Preview" className="h-full w-full object-contain" />
                                        ) : (
                                            <span className="text-gray-400 text-sm">No Logo</span>
                                        )}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">{languageManager.profile.logo_label}</label>
                                    <p className="text-[11px] text-gray-500">{languageManager.profile.logo_note}</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div className="col-span-2">
                                    <label className="block text-xs font-medium text-gray-700 mb-0.5">{languageManager.profile.user_name_label}</label>
                                    {loading ? (
                                        <div className="h-10 bg-slate-100 dark:bg-slate-800 rounded-lg w-full"></div>
                                    ) : (
                                        <input
                                            type="text"
                                            readOnly
                                            disabled
                                            className="w-full px-3 py-1.5 rounded-lg border border-gray-200 bg-gray-50 text-gray-600 cursor-not-allowed text-sm"
                                            value={user?.email || ''}
                                        />
                                    )}
                                </div>

                                <div className="col-span-2">
                                    <label className="block text-xs font-medium text-gray-700 mb-0.5">{languageManager.landing.demo_request.form.company}</label>
                                    {loading ? (
                                        <div className="h-10 bg-slate-100 dark:bg-slate-800 rounded-lg w-full"></div>
                                    ) : (
                                        <input
                                            type="text"
                                            readOnly
                                            disabled
                                            className="w-full px-3 py-1.5 rounded-lg border border-gray-200 bg-gray-50 text-gray-600 cursor-not-allowed text-sm"
                                            value={profile.company_name}
                                        />
                                    )}
                                </div>

                                <div className="col-span-2">
                                    <label className="block text-xs font-medium text-gray-700 mb-0.5">{languageManager.profile.address}</label>
                                    {loading ? (
                                        <div className="h-24 bg-slate-100 dark:bg-slate-800 rounded-lg w-full"></div>
                                    ) : (
                                        <textarea
                                            rows={2}
                                            readOnly
                                            disabled
                                            className="w-full px-3 py-1.5 rounded-lg border border-gray-200 bg-gray-50 text-gray-600 cursor-not-allowed text-sm"
                                            value={profile.address}
                                        />
                                    )}
                                </div>

                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-0.5">{languageManager.profile.phone}</label>
                                    {loading ? (
                                        <div className="h-10 bg-slate-100 dark:bg-slate-800 rounded-lg w-full"></div>
                                    ) : (
                                        <input
                                            type="text"
                                            readOnly
                                            disabled
                                            className="w-full px-3 py-1.5 rounded-lg border border-gray-200 bg-gray-50 text-gray-600 cursor-not-allowed text-sm"
                                            value={profile.phone}
                                        />
                                    )}
                                </div>

                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-0.5">{languageManager.profile.company_email_label}</label>
                                    {loading ? (
                                        <div className="h-10 bg-slate-100 dark:bg-slate-800 rounded-lg w-full"></div>
                                    ) : (
                                        <input
                                            type="text"
                                            readOnly
                                            disabled
                                            className="w-full px-3 py-1.5 rounded-lg border border-gray-200 bg-gray-50 text-gray-600 cursor-not-allowed text-sm"
                                            value={profile.company_email}
                                        />
                                    )}
                                </div>
                                


                            </div>

                            <div className="pt-3 border-t border-gray-100 flex items-center justify-between">
                                <div className="text-sm text-gray-500 w-full flex justify-between items-center">
                                    {profile.terms_accepted_at ? (
                                        <div className="flex items-center space-x-4">
                                            <span className="flex items-center text-green-600">
                                                <Check className="mr-1" /> {languageManager.profile.terms_accepted} {new Date(profile.terms_accepted_at).toLocaleDateString()}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => setShowTermsModal(true)}
                                                className="text-blue-600 hover:text-blue-800 underline text-sm font-medium"
                                            >
                                                {languageManager.profile.view_terms}
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="flex items-center space-x-4">
                                            <span className="text-orange-500 flex items-center">
                                                <AlertTriangle className="mr-1" /> {languageManager.profile.terms_not_accepted}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => setShowTermsModal(true)}
                                                className="text-blue-600 hover:text-blue-800 underline text-sm font-medium"
                                            >
                                                {languageManager.profile.review_accept}
                                            </button>
                                        </div>
                                    )}
                                    <div className="text-xs text-gray-400 italic">
                                        {languageManager.profile.admin_note}
                                    </div>
                                </div>
                            </div>

                        </form>
                    </div>
                </div>
            </main>

            {/* Terms Modal */}
            {showTermsModal && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-sm animate-in fade-in duration-300">
                    <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-md w-full p-8 border border-slate-200 dark:border-slate-700 transform transition-all scale-100">
                        <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">{languageManager.profile.modal_title}</h2>
                        <div className="text-sm text-slate-600 dark:text-slate-300 mb-6 bg-slate-50 dark:bg-slate-900 p-4 rounded-lg border border-slate-100 dark:border-slate-700 max-h-60 overflow-y-auto">
                            <p className="mb-3 font-medium">{languageManager.profile.modal_subtitle}</p>
                            <p className="mb-2">
                                {languageManager.profile.modal_agree
                                    .replace('{tos}', 'TERMS_PLACEHOLDER')
                                    .replace('{privacy}', 'PRIVACY_PLACEHOLDER')
                                    .split(/(TERMS_PLACEHOLDER|PRIVACY_PLACEHOLDER)/)
                                    .map((part, index) => {
                                        if (part === 'TERMS_PLACEHOLDER') {
                                            return (
                                                <button
                                                    key={index}
                                                    type="button"
                                                    onClick={() => navigate('/terms')}
                                                    className="text-blue-600 hover:underline font-bold bg-transparent border-none p-0 inline cursor-pointer"
                                                >
                                                    {languageManager.legal.tos_title}
                                                </button>
                                            );
                                        }
                                        if (part === 'PRIVACY_PLACEHOLDER') {
                                            return (
                                                <button
                                                    key={index}
                                                    type="button"
                                                    onClick={() => navigate('/privacy')}
                                                    className="text-blue-600 hover:underline font-bold bg-transparent border-none p-0 inline cursor-pointer"
                                                >
                                                    {languageManager.legal.privacy_title}
                                                </button>
                                            );
                                        }
                                        return part;
                                    })
                                }
                            </p>
                            <p className="text-xs text-slate-500 dark:text-slate-400 italic">
                                {languageManager.profile.modal_ack}
                            </p>
                        </div>

                        {modalError && (
                            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/40 border border-red-200 dark:border-red-700/50 rounded-lg text-red-600 dark:text-red-400 text-xs font-bold animate-in fade-in slide-in-from-top-2">
                                <AlertTriangle className="inline-block mr-2" size={14} />
                                {modalError}
                            </div>
                        )}

                        <label className="flex items-start gap-3 mb-6 cursor-pointer group p-3 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors border border-transparent hover:border-slate-100 dark:hover:border-slate-600">
                            <div className="relative flex items-center mt-0.5">
                                <input
                                    type="checkbox"
                                    className="w-5 h-5 border-2 border-slate-300 rounded text-blue-600 focus:ring-blue-500 focus:ring-offset-0 transition-all cursor-pointer"
                                    checked={termsAgreed}
                                    onChange={(e) => setTermsAgreed(e.target.checked)}
                                />
                            </div>
                            <span className="text-sm font-medium text-slate-700 dark:text-slate-200 group-hover:text-slate-900 dark:group-hover:text-white select-none">
                                {languageManager.profile.checkbox_label}
                            </span>
                        </label>

                        <div className="flex gap-3">
                            <button
                                type="button"
                                onClick={() => setShowTermsModal(false)}
                                className="flex-1 py-3 px-4 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 rounded-xl font-bold transition-all"
                            >
                                {languageManager.profile.cancel}
                            </button>
                            <button
                                type="button"
                                onClick={handleAcceptTerms}
                                disabled={!termsAgreed || isAccepting}
                                className="flex-1 py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-blue-600/20 flex items-center justify-center gap-2"
                            >
                                {isAccepting ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                        <span>{languageManager.profile.saving}</span>
                                    </>
                                ) : (
                                    languageManager.profile.accept
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Profile;
